import type AggregateError from 'es-aggregate-error';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { Campaign, DailyStat, Event, TAttributionModel } from '@feathr/blackbox';
import { reportModuleLabels, toDateStr } from '@feathr/blackbox';
import { Button, TableStatsCard, toast } from '@feathr/components';
import { useToggle } from '@feathr/hooks';
import type { IPagination, IRachisMessage, ListResponse } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';
import { useStore } from '@feathr/report_components/state';

import DailyStatsTable from '../DailyStatsTable';
import ExportModal from '../ExportModal';

export interface IDailyStatsTableCardProps {
  attributionModel?: TAttributionModel;
  end: string;
  object: Event | Campaign;
  showConversions?: boolean;
  showSpend?: boolean;
  start: string;
}

function DailyStatsTableCard({
  attributionModel,
  end,
  object,
  showConversions,
  showSpend,
  start,
}: Readonly<IDailyStatsTableCardProps>): JSX.Element {
  const { Stats, FacebookCampaignInsights } = useStore();
  const [showExportModal, toggleExportModal] = useToggle(false);
  const { t } = useTranslation();
  const [stats, setStats] = useState<ListResponse<DailyStat>>(Stats.newListResponse());
  const [pagination, setPagination] = useState<IPagination>({
    page: 0,
    page_size: 20,
  });
  const className = object.attributes._cls;
  const isFacebookCampaign = className.includes('Facebook');

  const getStats = useCallback(() => {
    const now = moment.utc();
    // the "end" prop refers to the campaign's end date which leads to incorrect pages calculations
    // due due to daily stats objects that have not yet been generated.
    const campaignEndDate = moment.utc(end).isAfter(now) ? now : moment.utc(end);
    const stats = Stats.list({
      filters: {
        metadata__date__lte: toDateStr(campaignEndDate),
        metadata__date__gte: start,
        metadata__obj_id: object.id,
      },
      pagination,
      model: className.includes('Event') ? 'event' : 'campaign',
    });

    setStats(stats);
  }, [Stats, end, start, object.id, className, pagination]);

  useEffect(() => {
    getStats();
  }, [getStats, pagination]);

  async function handleConfirmExport(email: string): Promise<IRachisMessage | AggregateError> {
    const exportParams = new URLSearchParams({
      daily: '1',
      start,
      end,
      email,
    });
    const response = await wretch<IRachisMessage>(
      `${object.collection!.url('export', object.id)}?${exportParams.toString()}`,
      {
        method: 'GET',
        headers: object.collection!.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      return response.error;
    }
    return response.data;
  }

  async function handleSyncFacebookCampaignStats(): Promise<void> {
    const params = new URLSearchParams({
      campaign_id: object.id,
    });
    const response = await FacebookCampaignInsights.sync(params);
    if (isWretchError(response)) {
      toast(
        t('Something went wrong while syncing stats: {{- message}}', {
          message: response.error.message,
        }),
        { type: ToastType.ERROR },
      );
    } else {
      toast(t('Campaign stats syncing is in progress. Please check back in a few minutes.'), {
        type: 'success',
      });
    }
  }

  const actions = [
    isFacebookCampaign && (
      <Button key={'sync'} onClick={handleSyncFacebookCampaignStats}>
        {t('Sync stats')}
      </Button>
    ),
    <Button key={'export'} onClick={toggleExportModal}>
      {t('Export to CSV')}
    </Button>,
  ];

  function onPageSizeChange(newPageSize: number, newPage: number): void {
    setPagination({ page: newPage, page_size: newPageSize });
  }

  function onPageChange(newPage: number): void {
    setPagination({ page: newPage, page_size: pagination.page_size });
  }

  return (
    <>
      <TableStatsCard actions={actions} title={reportModuleLabels.includeDailyStatsTable}>
        <DailyStatsTable
          attributionModel={attributionModel ?? 'full'}
          isPending={stats.isPending}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pagination={pagination}
          showConversions={showConversions}
          showSpend={showSpend}
          stats={stats}
        />
      </TableStatsCard>
      {showExportModal && (
        <ExportModal
          onClose={toggleExportModal}
          onConfirm={handleConfirmExport}
          title={t('Export Daily Stats')}
        />
      )}
    </>
  );
}

export default observer(DailyStatsTableCard);
